import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import AppCell from '@/components/common/app-cell';
import DurationDisplay from '@/components/common/duration-display';
import ScrollSelector from '@/components/common/ScrollSelector';
import Toggle from '@/components/common/toggle';
import { ContactListItem } from '@/components/contacts/contact-list/contact-list-item';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useClearSession from '@/hooks/conversations/use-clear-session';
import useSessionSettings from '@/hooks/sessions/use-session-settings';
import useUpdateSessionDnd from '@/hooks/sessions/use-update-session-dnd';
import useUpdateSessionReadDelete, { AUTO_DELETE_TIMES } from '@/hooks/sessions/use-update-session-read-delete';
import useUpdateSessionTop from '@/hooks/sessions/use-update-session-top';
import { useIsDesktop } from '@/hooks/use-user-platform';
import useFriendInfo from '@/hooks/users/use-friend-info';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { getDurationFormatKey } from '@/utils/datetime';
import { translateComplexKey } from '@/utils/translate';

export default function SessionSettingsByIdRoute() {
  const { id: sessionId } = useTypedParams(ROUTES.MESSAGES.SESSIONS.SETTINGS);

  const { session, setting } = useSessionSettings('session', sessionId);

  const { t } = useTranslation();
  const [showFlyout, setShowFlyout] = useState(false);
  const { mutateAsync: setDoNotDisturb, isPending: isUpdatingDnD } = useUpdateSessionDnd('session', sessionId);
  const { mutateAsync: updateSessionTop, isPending: isUpdatingTop } = useUpdateSessionTop('session', sessionId);
  const { clearSession } = useClearSession('session', sessionId);
  const {
    setReadDelete,
    setReadDeleteDuration,
    isPending: isUpdatingReadDelete,
  } = useUpdateSessionReadDelete(sessionId);
  const { friendInfo } = useFriendInfo(session?.UserID);
  const isDesktop = useIsDesktop();

  const handleClearMessages = () =>
    makePrompt({
      key: 'clear-messages',
      type: 'dialog',
      title: t('conversation.delete-all-confirm'),
      content: t('conversation.delete-all-confirm-content'),
      promise: () => clearSession(false),
    }).catch(console.error);

  return (
    <PageLayout
      title={t('conversation.setting.title')}
      className='gap-0'
      headerConfig={{
        navigateBack: true,
        className: 'desktop:hidden',
      }}>
      <div className='flex-1 overflow-y-auto flex flex-col basis-50dvh justify-start'>
        <div className='mx-2 desktop:hidden'>
          {friendInfo?.FriendInfo && (
            <Link
              className='flex items-center'
              to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({ params: { userId: session?.UserID ?? 0 } })}
              state={ROUTES.CONTACT.USERS.BY_ID.$buildState({ state: { from: 'session' } })}>
              <AppCell
                label={
                  <ContactListItem
                    item={{
                      ID: session?.UserID ?? 0,
                      FriendNickName: friendInfo.FriendInfo.FriendNickName ?? '',
                      FriendHeadImg: friendInfo.FriendInfo.FriendHeadImg ?? '',
                      RemarkName: friendInfo.FriendInfo.RemarkName || '',
                      IsOnline: friendInfo.FriendInfo.IsOnline,
                    }}
                    className='p-0'
                  />
                }
                chevron
              />
            </Link>
          )}
        </div>

        <hr className='mx-4 mt-2 desktop:hidden' />

        <div className='mx-2 flex flex-col desktop:mx-0'>
          <Link to={ROUTES.MESSAGES.SESSIONS.MEDIA.$buildPath({ params: { id: sessionId } })}>
            <AppCell
              chevron={!isDesktop}
              label={t('my-collection.media')}
            />
          </Link>
        </div>

        <hr className='mx-4 desktop:hidden' />

        <div className='mx-2 flex flex-col desktop:mx-0'>
          <AppCell
            label={t('conversation.setting.vanish-mode')}
            suffix={
              <Toggle
                checked={setting?.ReadDelete}
                isLoading={isUpdatingReadDelete}
                onChange={setReadDelete}
              />
            }
          />

          {setting?.ReadDelete && (
            <AppCell
              label={t('conversation.setting.vanish-time')}
              onClick={() => setShowFlyout(true)}
              suffix={
                <DurationDisplay
                  duration={setting?.AutoDeleteTime}
                  className='text-secondary'
                />
              }
            />
          )}

          <AppCell
            label={t('conversation.setting.mute')}
            suffix={
              <Toggle
                checked={setting?.IsNotDisturb}
                isLoading={isUpdatingDnD}
                onChange={(value) => setDoNotDisturb(value)}
              />
            }
          />

          <AppCell
            label={t('conversation.setting.pin')}
            suffix={
              <Toggle
                checked={setting?.IsTop}
                isLoading={isUpdatingTop}
                onChange={(value) => updateSessionTop(value)}
              />
            }
          />
        </div>

        <hr className='mx-4 mt-2 desktop:pt-0 desktop:mx-0' />

        <div className='mx-2 flex flex-col desktop:mx-0'>
          <Link to={ROUTES.MESSAGES.SESSIONS.SEARCH.$buildPath({ params: { id: sessionId } })}>
            <AppCell
              label={t('conversation.setting.search')}
              chevron={!isDesktop}
            />
          </Link>

          <AppCell
            label={t('conversation.setting.clear-all')}
            onClick={handleClearMessages}
          />
        </div>

        <hr className='mx-4 mt-2 desktop:pt-0 desktop:mx-0' />

        <div className='mx-2 flex flex-col desktop:mx-0'>
          <Link to={ROUTES.MESSAGES.SESSIONS.REPORT.$buildPath({ params: { id: sessionId } })}>
            <AppCell
              label={t('conversation.setting.report')}
              chevron={!isDesktop}
            />
          </Link>
        </div>
      </div>

      <ScrollSelector
        show={showFlyout}
        onCancel={() => setShowFlyout(false)}
        onConfirm={(time) => {
          setReadDeleteDuration(Number(time)).catch(console.warn);
          setShowFlyout(false);
        }}
        title={t('conversation.setting.vanish-time')}
        defaultValue={setting?.AutoDeleteTime.toString()}
        items={AUTO_DELETE_TIMES.map((time) => ({
          key: `auto-delete-time-${time}`,
          label: translateComplexKey(getDurationFormatKey(time), t),
          value: time.toString(),
        }))}
      />
    </PageLayout>
  );
}
